import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useState } from "react";
import { PERSONAL_WORK_ACCESS_FLAG } from "../../../shared/featureFlagNames";
import { sharedMutators } from "../../../shared/mutators";
import { MAX_COMPANY_NAME_LENGTH } from "../../../shared/mutators/setCompanyName";
import { isPersonalWorkEnabled } from "../../../shared/personalWork";
import { queries } from "../../../shared/queries";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useSessionTenantOrigin } from "../../hooks/useSessionTenantOrigin";
import { activeTenantAdminUsers } from "../../lib/activeTenantAdminUsers";
import { emitErrorToast } from "../../lib/errorToast";
import { useMakeUrl, routes } from "../../lib/urls";
import { userDisplayName } from "../../lib/userDisplayName";
import { classifyZeroResult } from "../../lib/zeroResultError";
import { Avatar } from "../Avatar";
import { ButtonLink } from "../Button/ButtonLink";
import { HelpTooltip } from "../HelpTooltip";
import { Input } from "../Input";
import { Text } from "../Text";
import { Toggle } from "../Toggle";
import {
  PanelCard,
  PanelContentBox,
  PanelHeader,
  PanelShell,
} from "./SettingsPanel";
import {
  DetailLabel,
  DetailList,
  DetailRow,
  DetailRowRight,
  RowNameContent,
  RowNameLayout,
  SectionHeading,
} from "./shared";

const CompanySection = styled(PanelContentBox)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CompanyDetails = styled(DetailList)`
  container-type: inline-size;
`;

const CompanyDetailRow = styled(DetailRow)`
  @container (max-width: 420px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const CompanyDetailValue = styled(DetailRowRight)`
  width: 280px;
  max-width: 100%;
  min-width: 0;

  @container (max-width: 420px) {
    width: 100%;
    margin-left: 0;
  }
`;

const CompanyInput = styled(Input)`
  width: 100%;
  min-width: 0;

  &[readonly] {
    background-color: var(--alpha4);
  }
`;

export function CompanySettings() {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const tenantOrigin = useSessionTenantOrigin();
  const isTenantAdmin = useHasAccess("allow_all");
  const personalWorkAccessAvailable =
    useFeatureFlag(PERSONAL_WORK_ACCESS_FLAG) === true;
  const [tenant] = useQuery(queries.currentTenant());
  const [adminGrants = []] = useQuery(
    isTenantAdmin ? queries.tenantAdminGrants() : undefined
  );
  const administrators = activeTenantAdminUsers(adminGrants).sort((a, b) =>
    userDisplayName(a).localeCompare(userDisplayName(b))
  );
  const [draft, setDraft] = useState<{
    companyName: string;
    previousCompanyName: string | null;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const companyName = draft?.companyName ?? tenant?.companyName ?? "";

  async function saveName() {
    if (!isTenantAdmin || !tenant || saving || !draft) return;
    const name = draft.companyName.trim();
    if (!name || name === (tenant.companyName ?? "")) {
      setDraft(null);
      setError("");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await z.mutate(
        sharedMutators.setCompanyName({
          companyName: name,
          previousCompanyName: draft.previousCompanyName,
        })
      ).server;
      if (result.type === "error") {
        const decision = classifyZeroResult(result);
        setError(
          (decision.kind === "toast" && decision.message) ||
            "Couldn’t save the company name. Try again."
        );
      } else {
        setDraft(null);
      }
    } catch {
      const message = "Couldn’t save the company name. Try again.";
      setError(message);
      emitErrorToast({ icon: "circle-off", message, buttonLabel: "Dismiss" });
    }
    setSaving(false);
  }

  return (
    <PanelShell>
      <PanelCard>
        <PanelHeader>
          <Text variant="display-md">Tenant</Text>
          <div style={{ marginTop: 2 }}>
            <Text variant="text-regular" color="var(--text-secondary)">
              {isTenantAdmin
                ? "Manage your tenant’s details, admins, and settings."
                : "Only tenant admins can manage these settings."}
            </Text>
          </div>
        </PanelHeader>
        {isTenantAdmin && tenant && (
          <>
            <CompanySection>
              <SectionHeading>
                <Text variant="text-regular-md">Tenant details</Text>
                <Text variant="text-small" color="var(--text-secondary)">
                  Your company name and tenant URL.
                </Text>
              </SectionHeading>
              <CompanyDetails>
                <CompanyDetailRow>
                  <DetailLabel>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <label htmlFor="company-name">
                        <Text variant="text-regular-md">Company name</Text>
                      </label>
                      <HelpTooltip content="New members see this name when completing their profile." />
                    </div>
                  </DetailLabel>
                  <CompanyDetailValue>
                    <CompanyInput
                      id="company-name"
                      autoComplete="organization"
                      aria-describedby={
                        error ? "company-name-error" : undefined
                      }
                      aria-invalid={!!error}
                      value={companyName}
                      onChange={(event) => {
                        setDraft({
                          companyName: event.target.value,
                          previousCompanyName: draft
                            ? draft.previousCompanyName
                            : (tenant.companyName ?? null),
                        });
                        setError("");
                      }}
                      onBlur={saveName}
                      onKeyDown={(event) => {
                        if (
                          event.key === "Enter" &&
                          !event.nativeEvent.isComposing
                        ) {
                          event.currentTarget.blur();
                        }
                      }}
                      disabled={saving}
                      required
                      maxLength={MAX_COMPANY_NAME_LENGTH}
                    />
                    {error && (
                      <div
                        id="company-name-error"
                        role="alert"
                        style={{ width: "100%" }}
                      >
                        <Text variant="text-small" color="var(--text-danger)">
                          {error}
                        </Text>
                      </div>
                    )}
                  </CompanyDetailValue>
                </CompanyDetailRow>
                <CompanyDetailRow>
                  <DetailLabel>
                    <label htmlFor="company-app-url">
                      <Text variant="text-regular-md">Tenant URL</Text>
                    </label>
                  </DetailLabel>
                  <CompanyDetailValue>
                    <CompanyInput
                      id="company-app-url"
                      value={tenantOrigin}
                      readOnly
                    />
                  </CompanyDetailValue>
                </CompanyDetailRow>
              </CompanyDetails>
            </CompanySection>
            <CompanySection>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                }}
              >
                <SectionHeading>
                  <Text variant="text-regular-md">Tenant admins</Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Manage who has full access to your tenant.
                  </Text>
                </SectionHeading>
                <ButtonLink
                  to={makeUrl(routes.modal.admins)}
                  variant="subtle"
                  aria-label="Manage tenant admins"
                >
                  Manage
                </ButtonLink>
              </div>
              {administrators.length > 0 && (
                <DetailList>
                  {administrators.map((user) => (
                    <DetailRow key={user.id}>
                      <RowNameLayout>
                        <Avatar user={user} size={32} />
                        <RowNameContent>
                          <Text variant="text-regular-md" ellipsis>
                            {userDisplayName(user)}
                          </Text>
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                            ellipsis
                          >
                            {user.email}
                          </Text>
                        </RowNameContent>
                      </RowNameLayout>
                    </DetailRow>
                  ))}
                </DetailList>
              )}
            </CompanySection>
            {personalWorkAccessAvailable && (
              <CompanySection>
                <SectionHeading>
                  <Text variant="text-regular-md">Personal work</Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Control work that happens outside shared spaces.
                  </Text>
                </SectionHeading>
                <DetailList>
                  <DetailRow>
                    <DetailLabel>
                      <Text variant="text-regular-md">Allow personal work</Text>
                      <Text variant="text-small" color="var(--text-secondary)">
                        Let members use Chat, build personal workflows, and use
                        connectors outside shared spaces
                      </Text>
                    </DetailLabel>
                    <DetailRowRight>
                      <Toggle
                        ariaLabel="Allow personal work"
                        isOn={isPersonalWorkEnabled(tenant)}
                        onToggle={() =>
                          z.mutate(
                            sharedMutators.setPersonalWorkAccess({
                              enabled: !isPersonalWorkEnabled(tenant),
                              previousEnabled: isPersonalWorkEnabled(tenant),
                            })
                          )
                        }
                      />
                    </DetailRowRight>
                  </DetailRow>
                </DetailList>
              </CompanySection>
            )}
          </>
        )}
      </PanelCard>
    </PanelShell>
  );
}
